import { cancelPendingAnnouncement } from './announcer'
import { isLiftPress, onWindowFocusIn, onWindowKeyDown, startKeyboardDrag } from './keyboard'
import { isDragPress, onPointerCancel, onPointerMove, onPointerUp, pressDraggable } from './pointer'
import { toGesture } from './scope'
import { attachWindowListeners, endGesture, runningGesture, type WindowListeners } from './session'

// Framework-free drag-and-drop engine. There is no provider and no mount step.
// Window listeners and the source-class observer exist only while a gesture runs.
// Draggables and droppables pay nothing at rest. Drag-state listeners exist only
// for callers that subscribed, and fire when a drag starts or ends. The React
// bindings only stamp data attributes, forward the initial press, and register
// drop handlers from a ref callback.

export * from './attributes'
export { registerDndElementHandlers, registerDndKit } from './registry'
export { isDraggedPayload, subscribeDraggedPayload } from './session'
export type * from './types'

const windowListeners: WindowListeners = {
    pointermove: onPointerMove,
    pointerup: onPointerUp,
    pointercancel: onPointerCancel,
    keydown: onWindowKeyDown,
    // Focus left the page mid-gesture, so the matching pointerup may never
    // arrive; cancel rather than leak a stuck drag.
    blur: () => endGesture(),
    focusin: onWindowFocusIn,
    // A press on an image, a link, or selected text would otherwise start the
    // browser's own drag, which fires pointercancel and ends this gesture.
    dragstart: event => event.preventDefault(),
}

type DraggableNotification<E> = {
    kitId: string
    payload: unknown
    event: E
    sourceElement: HTMLElement
}

export function notifyDraggablePointerDown(args: DraggableNotification<PointerEvent>): void {
    const { kitId, payload, event, sourceElement } = args
    // A press while a gesture runs must never start or hijack another (covers multi-touch).
    if (runningGesture() !== null) return
    if (!isDragPress(event, sourceElement)) return
    pressDraggable(toGesture(kitId, payload, sourceElement), event)
    attachWindowListeners(windowListeners)
}

export function notifyDraggableKeyDown(args: DraggableNotification<KeyboardEvent>): void {
    const { kitId, payload, event, sourceElement } = args
    // Lift keys only; in-gesture arrows/confirm/cancel are owned by the one
    // global keydown handler, so per-item cost stays at a single React prop.
    if (!isLiftPress(event, sourceElement)) return
    if (runningGesture() !== null) return
    // preventDefault doubles as the lift marker: the window handler attached
    // by the lift still sees this keypress and skips it, so it never also drops.
    event.preventDefault()
    attachWindowListeners(windowListeners)
    startKeyboardDrag(toGesture(kitId, payload, sourceElement))
}

/** Cancels any gesture, pending announcement, and window listeners. Kits stay registered. */
export function resetDndEngine(): void {
    endGesture()
    cancelPendingAnnouncement()
}
